/**
 * http — owns the bare "fetch → check ok → parse JSON" mechanic for simple GET
 * endpoints, so the try/catch + resp.ok + resp.json() boilerplate isn't
 * re-implemented per call site. Richer clients keep their own wrappers.
 */

/**
 * GET `url`, parse the JSON body. Soft-fails: returns null on HTTP error,
 * network failure, abort, or unparseable body — for callers that fall back
 * to a default rather than surfacing the error.
 */
export async function getJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const resp = await fetch(url, init);
    if (!resp.ok) return null;
    return await resp.json() as T;
  } catch {
    return null;
  }
}

/**
 * GET `url`, parse the JSON body. Throws `<label> failed (<status>)` on an
 * HTTP error — for callers that surface failures. Network errors propagate.
 */
export async function getJsonOrThrow<T>(url: string, label: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(url, init);
  if (!resp.ok) throw new Error(`${label} failed (${resp.status})`);
  return await resp.json() as T;
}

/**
 * Fetch `url` (any method via `init`) and return the HTTP status, ok flag and
 * parsed JSON body (null when the body is not JSON). Lets callers branch on a
 * specific status or error-body shape while sharing the fetch -> parse mechanic.
 */
export async function getJsonResult<T>(
  url: string,
  init?: RequestInit,
): Promise<{ ok: boolean; status: number; json: T | null }> {
  const resp = await fetch(url, init);
  let json: T | null = null;
  try { json = (await resp.json()) as T; } catch { /* non-JSON body */ }
  return { ok: resp.ok, status: resp.status, json };
}

/**
 * The readable message in a FastAPI error body, or null when it carries none.
 *
 * Three shapes reach here. An error the service raises itself is
 * `{ detail: "…" }`, or `{ detail: { message: "…", … } }` when the page acts
 * on the rest of the object (Monte Carlo's unpriced-share refusal). A request that breaks the pydantic model is
 * `{ detail: [{ loc, msg, input }, …] }` — and reading only the string shape
 * is how a 46-character ticker against a 24-character cap reached the
 * Projections tab as a bare "returned 422" (2026-10-10). Each item becomes
 * `holdings[0].ticker: <msg> (got "<input>")`, the leading "body" dropped.
 */
export function fastApiDetailMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object' || !('detail' in body)) return null;
  const detail = (body as { detail: unknown }).detail;
  if (typeof detail === 'string') return detail;
  // A refusal the page acts on carries an object with its words under
  // `message` (python-compute app/errors.py AppError).
  if (detail && typeof detail === 'object' && !Array.isArray(detail)
    && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message;
  }
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const parts = detail.slice(0, 3).map((item) => {
    if (!item || typeof item !== 'object') return String(item);
    const { loc, msg, input } = item as { loc?: unknown; msg?: unknown; input?: unknown };
    const path = Array.isArray(loc)
      ? loc
          .filter((p, i) => !(i === 0 && p === 'body'))
          .map((p, i) => (typeof p === 'number' ? `[${p}]` : `${i === 0 ? '' : '.'}${String(p)}`))
          .join('')
      : '';
    const got = typeof input === 'string' || typeof input === 'number'
      ? ` (got ${JSON.stringify(input)})`
      : '';
    const text = typeof msg === 'string' ? msg : 'invalid value';
    return `${path ? `${path}: ` : ''}${text}${got}`;
  });
  const more = detail.length > 3 ? ` (+${detail.length - 3} more)` : '';
  return `Request rejected — ${parts.join('; ')}${more}`;
}

/**
 * POST a JSON body and return the parsed JSON response plus HTTP status. On a
 * non-2xx response, extracts the FastAPI error message (`fastApiDetailMessage`,
 * both the string and the validation-list shapes; falls back to raw text) and
 * throws the Error produced by `fail`.
 */
export async function postJsonForResult(
  url: string,
  body: unknown,
  fail: (detailMsg: string | null, status: number, detail: unknown) => Error,
  signal?: AbortSignal,
): Promise<{ json: unknown; status: number }> {
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = await resp.json();
    } catch {
      detail = await resp.text().catch(() => undefined);
    }
    throw fail(fastApiDetailMessage(detail), resp.status, detail);
  }
  return { json: (await resp.json()) as unknown, status: resp.status };
}
